import { memo, useMemo, useState } from 'react';

import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { FieldSettings, FilterChangeEvent } from '@progress/kendo-react-data-tools';
import styled from 'styled-components';

import { useEvent } from 'core/hooks';
import { Button, Filter, Window, WindowActionsBar } from 'core/ui';
import { getDateForUI } from 'core/utils';

import { RelativeDateFilter } from '../../worklist/fragments/RelativeDateFilter';
import { FilterModalProps } from '../types';

/**
 * Recursively processes filter values to ensure Date objects are properly converted
 */
function processFilterForUI(filter: CompositeFilterDescriptor | null): CompositeFilterDescriptor | null {
  if (!filter) return null;

  console.log('FilterModal - Processing filter for UI:', filter);

  const processedFilters = filter.filters
    .filter((f): f is NonNullable<typeof f> => f !== null) // Type guard to remove null filters
    .map((f) => {
      if ('filters' in f) {
        // Composite filter - recurse
        return processFilterForUI(f);
      } else {
        // Simple filter - check if it's a date field and convert value
        const fieldName = typeof f.field === 'string' ? f.field : '';
        const processedValue =
          fieldName &&
          [
            'dob',
            'slaDate',
            'studyDate',
            'uploadedDate',
            'dateCreated',
            'completedDate',
            'requestedDate',
            'assignedDate',
            'returnedDate',
            'uploadDate',
          ].includes(fieldName)
            ? getDateForUI(f.value)
            : f.value;

        console.log(`FilterModal - Processing field "${fieldName}":`, {
          original: f.value,
          originalType: typeof f.value,
          processed: processedValue,
          processedType: typeof processedValue,
        });

        return {
          ...f,
          field: fieldName,
          value: processedValue,
        };
      }
    })
    .filter((f): f is NonNullable<typeof f> => f !== null); // Type guard to remove any null results from recursion

  const processedFilter: CompositeFilterDescriptor = {
    ...filter,
    filters: processedFilters,
  };

  console.log('FilterModal - Processed filter result:', processedFilter);
  return processedFilter;
}

export const FilterModal = memo<FilterModalProps>(({ columns, initialFilter, onClose, onSave, relativeDateFilter }) => {
  // Process the initial filter to ensure date values are Date objects
  const processedInitialFilter = useMemo(() => {
    console.log('FilterModal - Processing initial filter:', initialFilter);
    return processFilterForUI(initialFilter);
  }, [initialFilter]);

  const [filter, setFilter] = useState<CompositeFilterDescriptor | null>(processedInitialFilter);

  const handleChange = useEvent((event: FilterChangeEvent) => {
    console.log('FilterModal - Filter changed:', event.filter);
    setFilter(event.filter);
  });

  const handleSaveClick = useEvent(() => {
    if (filter == null) {
      throw new Error('Filter cannot be null when saving.');
    }

    console.log('FilterModal - Saving filter:', filter);
    onSave(filter);
  });

  const fields: FieldSettings[] = useMemo(
    () =>
      columns
        .filter((column) => column.filter)
        .map((column) => ({
          name: column.field,
          label: column.title,
          filter: column.filter,
          operators: column?.operators ?? [],
        })),
    [columns],
  );

  if (filter == null) return null;

  console.log('FilterModal - Rendering with filter:', filter);

  return (
    <Window onClose={onClose} title="Filter" initialWidth={600}>
      <StyledDialogBodyDiv>
        {/* Relative Date Filter Section */}
        {relativeDateFilter && (
          <StyledDateFilterSection>
            <RelativeDateFilter {...relativeDateFilter} disabled={false} />
          </StyledDateFilterSection>
        )}

        {/* Regular Kendo Filters */}
        <Filter fields={fields} onChange={handleChange} value={filter} />
      </StyledDialogBodyDiv>
      <WindowActionsBar>
        <Button onClick={handleSaveClick}>Apply Filter</Button>
      </WindowActionsBar>
    </Window>
  );
});

FilterModal.displayName = 'FilterModal';

const StyledDialogBodyDiv = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledDateFilterSection = styled.div`
  margin-bottom: ${({ theme }) => theme.space.spacing20};
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
  padding-bottom: ${({ theme }) => theme.space.spacing20};
`;
